import { useEffect, useRef, useState, useCallback } from 'react'
import { X, Send, Minimize2 } from 'lucide-react'
import { useConfig } from '../../lib/config'

const ASSISTANT_AVATAR = '/tv-assistant-avatar.png'
const ASSISTANT_MASCOT = '/tv-assistant.png'

// ChatJS global types (loaded via script tag)
declare global {
  interface Window {
    connect?: {
      ChatSession: {
        setGlobalConfig: (config: { region: string }) => void
        create: (args: ChatSessionArgs) => ChatSession
      }
    }
  }
}

interface ChatSessionArgs {
  chatDetails: {
    contactId: string
    participantId: string
    participantToken: string
  }
  type: 'CUSTOMER'
}

interface ChatSession {
  connect: () => Promise<{ connectSuccess: boolean }>
  sendMessage: (args: { contentType: string; message: string }) => Promise<void>
  onMessage: (callback: (event: ChatMessageEvent) => void) => void
  onTyping: (callback: (event: TypingEvent) => void) => void
  onEnded: (callback: () => void) => void
  onConnectionEstablished: (callback: () => void) => void
  disconnectParticipant: () => Promise<void>
}

interface ChatMessageEvent {
  data: {
    Type: string
    Content?: string
    ContentType?: string
    ParticipantRole?: string
    DisplayName?: string
  }
}

interface TypingEvent {
  data: {
    ParticipantRole: string
  }
}

interface ChatMessage {
  id: string
  content: string
  sender: 'user' | 'bot' | 'agent' | 'system'
  timestamp: Date
}

interface StartChatResponse {
  success: boolean
  data?: {
    contactId: string
    participantId: string
    participantToken: string
  }
  error?: string
}

// Load ChatJS script dynamically
function loadChatJSScript(): Promise<void> {
  return new Promise((resolve, reject) => {
    if (window.connect?.ChatSession) {
      resolve()
      return
    }

    const existingScript = document.getElementById('amazon-connect-chatjs')
    if (existingScript) {
      // Script is loading, wait for it
      existingScript.addEventListener('load', () => resolve())
      existingScript.addEventListener('error', () => reject(new Error('Failed to load ChatJS')))
      return
    }

    const script = document.createElement('script')
    script.id = 'amazon-connect-chatjs'
    script.src = 'https://unpkg.com/amazon-connect-chatjs@5/dist/amazon-connect-chat.js'
    script.async = true
    script.onload = () => resolve()
    script.onerror = () => reject(new Error('Failed to load ChatJS'))
    document.head.appendChild(script)
  })
}

/**
 * Custom Amazon Connect Chat Widget using ChatJS
 *
 * Provides a custom chat interface that integrates with Amazon Connect
 * via the StartChatContact API and ChatJS library.
 */
export function ConnectChatWidget() {
  const { config, loading: configLoading } = useConfig()
  const [isOpen, setIsOpen] = useState(false)
  const [isMinimized, setIsMinimized] = useState(false)
  const [isConnecting, setIsConnecting] = useState(false)
  const [isConnected, setIsConnected] = useState(false)
  const [messages, setMessages] = useState<ChatMessage[]>([])
  const [inputValue, setInputValue] = useState('')
  const [isTyping, setIsTyping] = useState(false)
  const [error, setError] = useState<string | null>(null)

  const chatSessionRef = useRef<ChatSession | null>(null)
  const messagesEndRef = useRef<HTMLDivElement>(null)

  // Use runtime config instead of build-time VITE_* vars
  const chatEnabled = config?.chatEnabled ?? false
  const apiKey = config?.apiKey
  const connectRegion = config?.connectRegion || 'us-east-1'

  // Scroll to bottom when new messages arrive
  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' })
  }, [messages])

  const addMessage = useCallback((content: string, sender: ChatMessage['sender']) => {
    setMessages(prev => [
      ...prev,
      {
        id: `${Date.now()}-${Math.random()}`,
        content,
        sender,
        timestamp: new Date(),
      },
    ])
  }, [])

  const startChat = useCallback(async () => {
    if (!apiKey) {
      setError('Chat is not configured')
      return
    }

    setIsConnecting(true)
    setError(null)

    try {
      // Load ChatJS script
      await loadChatJSScript()

      if (!window.connect?.ChatSession) {
        throw new Error('ChatJS failed to initialize')
      }

      // Call our API to start the chat (via CloudFront /api/* proxy)
      const response = await fetch('/api/chat/start', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'x-api-key': apiKey,
        },
        body: JSON.stringify({
          displayName: 'Website Visitor',
        }),
      })

      const result: StartChatResponse = await response.json()

      if (!result.success || !result.data) {
        throw new Error(result.error || 'Failed to start chat')
      }

      // Initialize ChatJS
      window.connect.ChatSession.setGlobalConfig({
        region: connectRegion,
      })

      // Create chat session
      const chatSession = window.connect.ChatSession.create({
        chatDetails: {
          contactId: result.data.contactId,
          participantId: result.data.participantId,
          participantToken: result.data.participantToken,
        },
        type: 'CUSTOMER',
      })

      chatSessionRef.current = chatSession

      // Set up event handlers
      chatSession.onConnectionEstablished(() => {
        setIsConnected(true)
        setIsConnecting(false)
        // Don't add a welcome message here - the bot will send one via the contact flow
      })

      chatSession.onMessage((event: ChatMessageEvent) => {
        const { Type, Content, ParticipantRole } = event.data

        if (Type === 'MESSAGE' && Content) {
          if (ParticipantRole === 'CUSTOMER') {
            return // Don't show our own messages from the event
          }
          const sender: ChatMessage['sender'] = ParticipantRole === 'AGENT' ? 'agent' : 'bot'
          addMessage(Content, sender)
          setIsTyping(false)
        }
      })

      chatSession.onTyping((event: TypingEvent) => {
        if (event.data.ParticipantRole !== 'CUSTOMER') {
          setIsTyping(true)
          // Auto-hide typing indicator after 3 seconds
          setTimeout(() => setIsTyping(false), 3000)
        }
      })

      chatSession.onEnded(() => {
        setIsConnected(false)
        addMessage('Chat ended. Thank you for contacting us!', 'system')
        chatSessionRef.current = null
      })

      // Connect to chat
      const connectResult = await chatSession.connect()
      if (!connectResult.connectSuccess) {
        throw new Error('Failed to connect to chat')
      }
    } catch (err) {
      console.error('Error starting chat:', err)
      setError(err instanceof Error ? err.message : 'Failed to start chat')
      setIsConnecting(false)
    }
  }, [apiKey, connectRegion, addMessage])

  const sendMessage = useCallback(async () => {
    if (!inputValue.trim() || !chatSessionRef.current || !isConnected) return

    const message = inputValue.trim()
    setInputValue('')
    addMessage(message, 'user')

    try {
      await chatSessionRef.current.sendMessage({
        contentType: 'text/plain',
        message,
      })
    } catch (err) {
      console.error('Error sending message:', err)
      addMessage('Failed to send message. Please try again.', 'system')
    }
  }, [inputValue, isConnected, addMessage])

  const handleKeyPress = useCallback(
    (e: React.KeyboardEvent) => {
      if (e.key === 'Enter' && !e.shiftKey) {
        e.preventDefault()
        sendMessage()
      }
    },
    [sendMessage]
  )

  const openChat = useCallback(() => {
    setIsOpen(true)
    setIsMinimized(false)
    if (!isConnected && !isConnecting) {
      startChat()
    }
  }, [isConnected, isConnecting, startChat])

  const closeChat = useCallback(async () => {
    if (chatSessionRef.current && isConnected) {
      try {
        await chatSessionRef.current.disconnectParticipant()
      } catch (err) {
        console.error('Error disconnecting:', err)
      }
    }
    setIsOpen(false)
    setIsConnected(false)
    setMessages([])
    chatSessionRef.current = null
  }, [isConnected])

  // Don't render if config is loading or chat is not enabled
  if (configLoading || !chatEnabled) {
    return null
  }

  return (
    <>
      {/* Chat Button with Mascot */}
      {!isOpen && (
        <button
          onClick={openChat}
          className="fixed bottom-4 right-4 z-50 flex flex-col items-end focus:outline-none group"
          aria-label="Open chat"
        >
          {/* Speech Bubble */}
          <div className="mb-2 rounded-xl bg-white px-4 py-2 shadow-lg transition-transform group-hover:scale-105">
            <p className="text-sm font-medium text-gray-800">How can I help you?</p>
            {/* Bubble tail */}
            <div className="absolute -bottom-2 right-8 h-0 w-0 border-l-8 border-r-8 border-t-8 border-l-transparent border-r-transparent border-t-white" />
          </div>
          {/* Mascot */}
          <img
            src={ASSISTANT_MASCOT}
            alt="Chat assistant"
            className="h-24 w-24 animate-float object-contain drop-shadow-lg transition-transform group-hover:scale-110 group-hover:animate-none"
          />
        </button>
      )}

      {/* Chat Window */}
      {isOpen && (
        <div
          className={`fixed bottom-6 right-6 z-50 flex w-80 flex-col overflow-hidden rounded-lg bg-white shadow-2xl transition-all sm:w-96 ${
            isMinimized ? 'h-14' : 'h-[500px]'
          }`}
        >
          {/* Header */}
          <div className="flex items-center justify-between bg-[#0f172a] px-4 py-3 text-white">
            <div className="flex items-center gap-3">
              <img
                src={ASSISTANT_AVATAR}
                alt="Assistant"
                className="h-8 w-8 rounded-full object-cover"
              />
              <span className="font-medium">TruVine Assistant</span>
            </div>
            <div className="flex items-center gap-2">
              <button
                onClick={() => setIsMinimized(!isMinimized)}
                className="rounded p-1 hover:bg-[#1e293b]"
                aria-label={isMinimized ? 'Expand chat' : 'Minimize chat'}
              >
                <Minimize2 className="h-4 w-4" />
              </button>
              <button
                onClick={closeChat}
                className="rounded p-1 hover:bg-[#1e293b]"
                aria-label="Close chat"
              >
                <X className="h-4 w-4" />
              </button>
            </div>
          </div>

          {/* Chat Content */}
          {!isMinimized && (
            <>
              {/* Messages */}
              <div className="flex-1 overflow-y-auto bg-gray-50 p-4">
                {error && (
                  <div className="mb-4 rounded-lg bg-red-100 p-3 text-sm text-red-700">
                    {error}
                  </div>
                )}

                {isConnecting && (
                  <div className="flex items-center justify-center py-8">
                    <div className="h-8 w-8 animate-spin rounded-full border-4 border-blue-500 border-t-transparent" />
                  </div>
                )}

                {messages.map(msg => (
                  <div
                    key={msg.id}
                    className={`mb-3 ${
                      msg.sender === 'user' ? 'flex justify-end' : 'flex justify-start'
                    }`}
                  >
                    {(msg.sender === 'bot' || msg.sender === 'agent') && (
                      <img
                        src={ASSISTANT_AVATAR}
                        alt="Assistant"
                        className="mr-2 h-8 w-8 flex-shrink-0 rounded-full object-cover"
                      />
                    )}
                    <div
                      className={`max-w-[75%] rounded-lg px-4 py-2 ${
                        msg.sender === 'user'
                          ? 'bg-[#1D4ED8] text-white'
                          : msg.sender === 'system'
                            ? 'bg-gray-200 text-gray-600 text-sm italic'
                            : 'bg-white text-gray-800 shadow'
                      }`}
                    >
                      {msg.content}
                    </div>
                  </div>
                ))}

                {isTyping && (
                  <div className="mb-3 flex justify-start">
                    <img
                      src={ASSISTANT_AVATAR}
                      alt="Assistant"
                      className="mr-2 h-8 w-8 flex-shrink-0 rounded-full object-cover"
                    />
                    <div className="rounded-lg bg-white px-4 py-2 shadow">
                      <span className="flex gap-1">
                        <span className="h-2 w-2 animate-bounce rounded-full bg-gray-400" />
                        <span
                          className="h-2 w-2 animate-bounce rounded-full bg-gray-400"
                          style={{ animationDelay: '0.1s' }}
                        />
                        <span
                          className="h-2 w-2 animate-bounce rounded-full bg-gray-400"
                          style={{ animationDelay: '0.2s' }}
                        />
                      </span>
                    </div>
                  </div>
                )}

                <div ref={messagesEndRef} />
              </div>

              {/* Input */}
              <div className="border-t bg-white p-4">
                <div className="flex gap-2">
                  <input
                    type="text"
                    value={inputValue}
                    onChange={e => setInputValue(e.target.value)}
                    onKeyPress={handleKeyPress}
                    placeholder={isConnected ? 'Type a message...' : 'Connecting...'}
                    disabled={!isConnected}
                    className="flex-1 rounded-lg border border-gray-300 px-4 py-2 text-sm text-gray-900 focus:border-[#38BDF8] focus:outline-none focus:ring-1 focus:ring-[#38BDF8] disabled:bg-gray-100"
                  />
                  <button
                    onClick={sendMessage}
                    disabled={!isConnected || !inputValue.trim()}
                    className="rounded-lg bg-[#1D4ED8] px-4 py-2 text-white transition-colors hover:bg-[#1e40af] disabled:cursor-not-allowed disabled:bg-gray-300"
                    aria-label="Send message"
                  >
                    <Send className="h-4 w-4" />
                  </button>
                </div>
              </div>
            </>
          )}
        </div>
      )}
    </>
  )
}
